import { css } from "@compiled/react";
import { isRichTextValueConsideredEmpty } from "@uniformdev/richtext";

import { RichTextComponent } from "@/libraries/design/utils";
import { TypographyStyles } from "@/libraries/wpl/foundations/src";

import { useStylePropertyVariableValueFromParent } from "../../../hooks/use-style-property-variable-value-from-parent";

type TextAlignment = "left" | "center" | "right";

export interface UniformTextProps {
    text?: string;
    uniformRichText?: unknown;
    style:
        | "body-lg"
        | "body-md"
        | "body-sm"
        | "body-lg bold"
        | "body-md bold"
        | "body-sm bold"
        | "card-title"
        | "card-title-sm"
        | "fine-print"
        | "fine-print-sm"
        | "eyebrow-lg"
        | "eyebrow-md"
        | "eyebrow-sm"
        | "callout"
        | "callout-lg";
    alignment?: TextAlignment;
    tagType?: "p" | "dt" | "dd" | "h2" | "h3" | "h4" | "h5" | "h6";
}

export const UniformText = ({
    text,
    uniformRichText,
    style = "body-lg",
    alignment = "left",
    tagType
}: UniformTextProps) => {
    const hasRichText = !isRichTextValueConsideredEmpty(uniformRichText);
    // defaults to p when no tagType is provided
    // Use div instead of p when rich text is present to avoid nested <p> tags
    const Component =
        hasRichText && (!tagType || tagType === "p") ? "div" : tagType || "p";

    const [textRef, textColor] = useStylePropertyVariableValueFromParent(
        "--dxp-container-text-color"
    );

    const alignmentStyle = css({
        textAlign: alignment
    });
    const boldStyling = css({
        "&.bold": {
            fontWeight: 700
        }
    });
    const resetMargin = css({
        margin: 0
    });

    return (
        <TypographyStyles>
            <Component
                ref={textRef as React.LegacyRef<HTMLParagraphElement>}
                className={style}
                css={[
                    style?.includes("bold") && boldStyling,
                    resetMargin,
                    alignmentStyle
                ]}
                style={textColor ? { color: textColor } : undefined}
            >
                {!isRichTextValueConsideredEmpty(uniformRichText) ? (
                    <RichTextComponent uniformParameter="uniformRichText" />
                ) : (
                    text
                )}
            </Component>
        </TypographyStyles>
    );
};

export default UniformText;
